import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Target, CheckCircle2, Circle, Clock, ChevronRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const PRI_BADGE = {
  urgent:    "bg-rose-100 text-rose-700 border-rose-200",
  important: "bg-amber-100 text-amber-700 border-amber-200",
  normal:    "bg-slate-100 text-slate-600 border-slate-200",
};
const PRI_LBL = { urgent: "Urgent", important: "Important", normal: "" };

const fmtDeadline = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso + "T00:00:00");
    const now = new Date(); now.setHours(0,0,0,0);
    const days = Math.round((d.getTime() - now.getTime()) / 86400000);
    if (days === 0) return { label: "Aujourd'hui", late: false, today: true };
    if (days < 0) return { label: `Retard ${Math.abs(days)}j`, late: true, today: false };
    if (days <= 3) return { label: `Dans ${days}j`, late: false, today: false, soon: true };
    return { label: d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }), late: false, today: false };
  } catch { return null; }
};

export default function MyMissionsWidget() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todo"); // todo | done | all

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/plans-action/my-missions",
        { params: filter === "all" ? {} : { status: filter } });
      setMissions(data || []);
    } catch { setMissions([]); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const toggleDone = async (id) => {
    try { await api.post(`/plans-action/${id}/toggle`); load(); }
    catch { toast.error("Erreur"); }
  };

  const todoCount = missions.filter(m => !m.done).length;
  const doneCount = missions.filter(m => m.done).length;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="my-missions-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 grid place-items-center">
            <Target className="w-4 h-4"/>
          </div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900 leading-none">Mes missions</h2>
            <div className="text-[11px] text-slate-500 mt-0.5">Missions qui vous sont affectées personnellement</div>
          </div>
        </div>
        <div className="flex items-center gap-1 text-[11px] font-semibold">
          <button data-testid="mm-filter-todo" onClick={() => setFilter("todo")}
                  className={`px-2.5 py-1 rounded-full transition ${filter === "todo" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            À faire {todoCount > 0 && <span className="ml-1 opacity-90">({todoCount})</span>}
          </button>
          <button data-testid="mm-filter-done" onClick={() => setFilter("done")}
                  className={`px-2.5 py-1 rounded-full transition ${filter === "done" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            Terminées
          </button>
          <button data-testid="mm-filter-all" onClick={() => setFilter("all")}
                  className={`px-2.5 py-1 rounded-full transition ${filter === "all" ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            Toutes
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm text-center py-8">Chargement…</div>
      ) : missions.length === 0 ? (
        <div className="text-center py-8">
          <Sparkles className="w-8 h-8 mx-auto text-emerald-400 mb-2"/>
          <div className="text-sm text-slate-500">
            {filter === "todo" ? "Aucune mission à faire — bien joué ! 🎉" :
             filter === "done" ? "Aucune mission terminée pour le moment." :
             "Aucune mission affectée."}
          </div>
        </div>
      ) : (
        <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
          {missions.slice(0, 12).map(m => {
            const d = fmtDeadline(m.deadline);
            return (
              <button key={m.id} onClick={() => toggleDone(m.id)}
                      data-testid={`my-mission-${m.id}`}
                      className={`w-full flex items-start gap-3 p-3 rounded-xl border transition text-left ${m.done ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200 hover:bg-white hover:border-blue-200"}`}>
                {m.done ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5"/> : <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5"/>}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${m.done ? "text-slate-500 line-through" : "text-slate-900"}`}>{m.title}</div>
                  {m.description && !m.done && (
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{m.description}</div>
                  )}
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {m.priority !== "normal" && !m.done && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${PRI_BADGE[m.priority] || PRI_BADGE.normal}`}>
                        {PRI_LBL[m.priority]}
                      </span>
                    )}
                    {m.hour && (
                      <span className="text-[10px] text-slate-500 font-mono flex items-center gap-1">
                        <Clock className="w-3 h-3"/>{m.hour}
                      </span>
                    )}
                    {d && (
                      <span className={`text-[10px] font-semibold ${d.late ? "text-rose-600" : d.today ? "text-blue-600" : d.soon ? "text-amber-600" : "text-slate-500"}`}>
                        {d.label}
                      </span>
                    )}
                    {m.created_by_name && (
                      <span className="text-[10px] text-slate-400 ml-auto">
                        par {m.created_by_name}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {missions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span><b className="text-slate-900">{doneCount}</b> terminée{doneCount > 1 ? "s" : ""} · <b className="text-slate-900">{todoCount}</b> à faire</span>
          <Link to="/ops/interventions" className="font-bold text-blue-600 hover:underline flex items-center gap-1">
            Toutes les interventions <ChevronRight className="w-3 h-3"/>
          </Link>
        </div>
      )}
    </div>
  );
}
